import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactTooltip from 'react-tooltip';

import { addTargetCollegeAPI } from '../../../../actions/college-corner.actions';
import { showErrorToast, showSuccessToast } from '../../../../actions/notification-actions';
import Img from '../../../../components/Img';
import useIsMobile from '../../../../components/hooks/useIsMobile';
import { TARGET_COLLEGE_ADDED_TOAST_TITLE } from '../../../../components/Profile/CollegeCoachActivity/components/targetCollegeToast';
import styles from './AddToTargetListCell.scss';

export const ADD_TO_LIST_TOOLTIP_TEXT = 'Add to My Target Colleges List';
export const ADDED_TOOLTIP_TEXT = 'Added';

const ADD_ICON_SRC = '/img/v3/icons/icon-18px-circle-plus-teal.svg';
const CHECK_ICON_SRC = '/img/v4/icon-checkmark-grey.svg';
const ALREADY_ADDED_STATUS = 409;
const ADD_ERROR_MESSAGE = 'We couldn\'t add that team. Please try again.';

const buildAddedMessage = (collegeName) => {
    return `${collegeName || 'The college'} was added to My Target Colleges on your profile page`;
};

const AddToTargetListCell = ({
    clubId,
    collegeName,
    isTargetCollege,
    rowGender,
    viewerGender,
    'data-testid': dataTestId,
}) => {
    const dispatch = useDispatch();
    const { isMobileWidth, isTabletWidth } = useIsMobile();
    const [added, setAdded] = useState(!!isTargetCollege);
    const [isAdding, setIsAdding] = useState(false);

    const tooltipEnabled = !isMobileWidth && !isTabletWidth;

    useEffect(() => {
        setAdded(!!isTargetCollege);
    }, [clubId, isTargetCollege]);

    const canAddByGender = !!viewerGender && !!rowGender &&
        viewerGender.toLowerCase() === rowGender.toLowerCase();

    const handleClick = async () => {
        if (!clubId || isAdding || added || !canAddByGender) {
            return;
        }
        setIsAdding(true);
        setAdded(true);
        try {
            await dispatch(addTargetCollegeAPI(clubId));
            dispatch(showSuccessToast({
                title: TARGET_COLLEGE_ADDED_TOAST_TITLE,
                message: buildAddedMessage(collegeName),
            }));
        } catch (err) {
            if (err?.response?.status === ALREADY_ADDED_STATUS) {
                return;
            }
            setAdded(false);
            dispatch(showErrorToast({ message: ADD_ERROR_MESSAGE }));
        } finally {
            setIsAdding(false);
        }
    };

    const tooltipId = `cpp-college-teams-add-tooltip-${clubId}`;
    const tooltipText = added ? ADDED_TOOLTIP_TEXT : ADD_TO_LIST_TOOLTIP_TEXT;

    return (
        <span
            className={styles.cell}
            data-for={tooltipEnabled ? tooltipId : undefined}
            data-tip={tooltipEnabled && (added || canAddByGender) ? tooltipText : undefined}
        >
            {added ? (
                <>
                    <span className={styles.addedLabel} data-testid={dataTestId && `${dataTestId}.added-label`}>
                        Added
                    </span>
                    <Img
                        src={CHECK_ICON_SRC}
                        alt="Added"
                        className={styles.checkIcon}
                        data-testid={dataTestId && `${dataTestId}.added-check`}
                    />
                </>
            ) : (
                <>
                    <span className={styles.addLabel} data-testid={dataTestId && `${dataTestId}.add-label`}>
                        Add to List
                    </span>
                    <button
                        type="button"
                        className={styles.addButton}
                        onClick={handleClick}
                        disabled={isAdding || !canAddByGender}
                        aria-label={ADD_TO_LIST_TOOLTIP_TEXT}
                        data-testid={dataTestId && `${dataTestId}.add-btn`}
                    >
                        <Img src={ADD_ICON_SRC} alt="" className={styles.addIcon} />
                    </button>
                </>
            )}
            {tooltipEnabled && (
                <ReactTooltip
                    id={tooltipId}
                    place="top"
                    effect="solid"
                    backgroundColor="#d6dde5"
                    textColor="#000000"
                    arrowColor="#d6dde5"
                    className={styles.tooltip}
                />
            )}
        </span>
    );
};

AddToTargetListCell.defaultProps = {
    collegeName: null,
    isTargetCollege: false,
    rowGender: null,
    viewerGender: null,
    'data-testid': null,
};

AddToTargetListCell.propTypes = {
    clubId: PropTypes.number.isRequired,
    collegeName: PropTypes.string,
    isTargetCollege: PropTypes.bool,
    rowGender: PropTypes.string,
    viewerGender: PropTypes.string,
    'data-testid': PropTypes.string,
};

export default AddToTargetListCell;
